import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, Building2 } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const TRACKED_COMPANIES = [
  'NVIDIA', 'AMD', 'Intel', 'Apple', 'Microsoft', 'Google', 'Amazon', 'Meta',
  'Tesla', 'Qualcomm', 'TSMC', 'Samsung', 'Broadcom', 'Salesforce', 'Oracle',
  'IBM', 'CrowdStrike', 'Palo Alto Networks', 'Snowflake', 'Databricks',
]

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

interface CompanyData {
  name: string
  count: number
  articles: Array<{ title: string; source: string; timestamp: string; url?: string }>
}

export default function CompetitorIntelPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/news/feed', { params: { limit: 60 } })
      setArticles(data.articles || data.items || data || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const companies: CompanyData[] = TRACKED_COMPANIES.map(name => {
    const lower = name.toLowerCase()
    const matched = articles.filter(a => {
      const text = `${a.title || ''} ${a.summary || ''} ${a.description || ''}`.toLowerCase()
      return text.includes(lower)
    })
    return {
      name,
      count: matched.length,
      articles: matched
        .sort((a: any, b: any) => new Date(b.timestamp || b.published || 0).getTime() - new Date(a.timestamp || a.published || 0).getTime())
        .slice(0, 3)
        .map((a: any) => ({ title: a.title, source: a.source, timestamp: a.timestamp || a.published, url: a.url })),
    }
  })
    .filter(c => c.count > 0)
    .sort((a, b) => b.count - a.count)

  const filtered = companies.filter(c =>
    !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Building2 size={24} className="text-blue" /> Competitor Intelligence
          </h1>
          <p className="text-sm text-text-secondary mt-1">Big Tech tracking across 120+ feeds</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search companies..."
          className="input pl-9 py-2 text-xs"
        />
      </div>

      {loading && !articles.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Scanning feeds for competitor mentions...</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(company => (
          <div key={company.name} className="card card-hover p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-text-primary">{company.name}</h3>
              <span className="badge-blue text-[10px]">{company.count} mention{company.count !== 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-2">
              {company.articles.map((article, idx) => (
                <div key={idx} className="p-2 rounded-lg bg-surface-card border border-border">
                  <p className="text-xs text-text-primary leading-snug line-clamp-2">
                    {article.url ? (
                      <a href={article.url} target="_blank" rel="noopener" className="hover:underline">{article.title}</a>
                    ) : article.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-text-muted">
                    <span>{article.source}</span>
                    <span>&middot;</span>
                    <span>{relativeTime(article.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-sm text-text-muted">No competitor mentions found matching your search.</p>
        </div>
      )}
    </div>
  )
}
